/* eslint-disable @typescript-eslint/no-explicit-any */
import { branchEndpoint } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

// 1. Color Generator
const getUserColors = (name: string) => {
  const colors = [
    { bg: "#E3F2FD", text: "#1565C0" }, // Blue
    { bg: "#F3E5F5", text: "#7B1FA2" }, // Purple
    { bg: "#E8F5E9", text: "#2E7D32" }, // Green
    { bg: "#FFF3E0", text: "#EF6C00" }, // Orange
    { bg: "#FFEBEE", text: "#C62828" }, // Red
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

export default function useBranches(
  callBack?: (data: any) => void,
  filters: Record<string, any> = {}
) {
  const query = useQuery({
    queryKey: ["branches", filters],
    queryFn: async () => {
      const res = await axiosInstance.get(branchEndpoint, { params: filters });
      // Keep both the branches list and the new tenant-wide aggregates
      // (No Branch / Unassigned) the header picker shows next to the
      // synthetic options. Older deployments without `aggregates` fall
      // back to `null` so consumers can render gracefully.
      const branches = res.data.branches || res.data;
      const aggregates = res.data.aggregates ?? null;
      return { branches: Array.isArray(branches) ? branches : [], aggregates };
    },
  });

  useEffect(() => {
    if (query.data && callBack) {
      // Preserve legacy callBack contract: callers expect an array of branch
      // rows, not the new {branches, aggregates} envelope.
      callBack(query.data.branches);
    }
  }, [query.data, callBack]);

  // 2. Data Transformer
  const formattedBranches = useMemo(() => {
    if (!query.data?.branches) return [];

    return query.data.branches.map((branch: any) => {
      // ✅ FIX: Access 'userBranches' directly as they are flat objects
      const allUsers = branch.userBranches || [];

      const visibleUsers = allUsers.slice(0, 4).map((user: any) => {
        const userName = user.name || "Unknown"; // Direct access
        const { bg, text } = getUserColors(userName);

        return {
          initial: userName.charAt(0).toUpperCase(),
          name: userName,
          bgColor: bg,
          textColor: text,
        };
      });

      return {
        id: branch.id,
        branchName: branch.name,
        branchCode: branch.code || "N/A",
        city: branch.city || "-",
        state: branch.state || "-",
        totalLeads: branch._count?.leads || 0,
        status: (branch.isActive ? "Active" : "Inactive") as
          | "Active"
          | "Inactive",
        isDefault: Boolean(branch.isDefault),
        // Branch-based pipelines: NULL = default pipeline, a number = this branch
        // runs its own fork. Carried through so the row's fork button can show the
        // edit/manage view instead of a blank "create" form (isForked detection).
        pipelineId: branch.pipelineId ?? null,
        assignedUsers: visibleUsers,
        additionalUsersCount: Math.max(0, allUsers.length - 4),
      };
    });
  }, [query.data]);

  // Sorted alphabetically (case-insensitive) at the source so every dropdown
  // consumer (header branch picker, lead filter, dashboard exports, import
  // lead, etc.) shows branches in the same predictable order. Backend-defined
  // ordering doesn't carry meaning for these pickers — alphabetical is the
  // sane default and matches what users naturally expect when scanning a
  // long branch list.
  const branchSelects = useMemo(
    () =>
      (query.data?.branches ?? [])
        .map((val: any) => ({
          value: String(val.id),
          name: val.name,
          // Total leads for this branch — surfaced in the header dropdown
          // and any other picker that wants to show per-branch volume. Falls
          // back to 0 if the backend omits the aggregate (older payloads).
          leadCount: Number(val._count?.leads ?? 0),
          // Branch-based pipelines: NULL = default pipeline, a number = forked.
          // Lets consumers (e.g. automation branch pickers) tell forks apart.
          pipelineId: val.pipelineId ?? null,
        }))
        .sort((a: { name: string }, b: { name: string }) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        ),
    [query.data]
  );

  // Tenant-wide slice counts for the header picker's synthetic options
  // ("All Leads", "No Branch", "Unassigned"). Server: `aggregates` on
  // the branches response. Default to 0 so consumers don't have to null-check.
  const aggregates = useMemo(
    () => ({
      noBranchLeadCount: Number(query.data?.aggregates?.noBranchLeadCount ?? 0),
      unassignedLeadCount: Number(
        query.data?.aggregates?.unassignedLeadCount ?? 0,
      ),
      totalLeadCount: Number(query.data?.aggregates?.totalLeadCount ?? 0),
    }),
    [query.data],
  );

  // Branch-based pipelines: true iff ANY branch runs its own forked pipeline.
  // The single gate for all forked-branch UI — when false, the tenant has no
  // fork and every consumer must behave exactly as before (zero-impact).
  const hasForkedBranch = useMemo(
    () => (query.data?.branches ?? []).some((b: any) => b?.pipelineId != null),
    [query.data],
  );

  return {
    branches: formattedBranches,
    branchSelects,
    aggregates,
    hasForkedBranch,
    length: branchSelects?.length,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
